/**
 * cart-delivery.js
 * ─────────────────────────────────────────────────────────────────
 * Pincode-based delivery date checker for the cart drawer.
 * Depends on: cart-shared.js  (isWidgetEnabled, plumGetSharedCart)
 * ─────────────────────────────────────────────────────────────────
 */
(function () {
  if (!isWidgetEnabled('delivery_block')) return;

  const SELECTOR  = '.cart-drawer-delivery .new-pincode-outer.cart-drawer-only';
  const CACHE_KEY = 'plumCartPinResult';

  // ─── Event delegation ────────────────────────────────────────────────────

  document.addEventListener('click', function (e) {
    const btn = e.target.closest(SELECTOR + ' .delivery-check');
    if (!btn) return;
    const els = plumEls(btn);
    if (!els) return;
    btn.classList.contains('is-change') ? plumReset(els, true) : plumValidate(els);
  });

  document.addEventListener('input', function (e) {
    const input = e.target.closest(SELECTOR + ' .pincode_input');
    if (!input) return;
    const els = plumEls(input);
    if (!els) return;
    if (input.value.trim().length < 6) plumReset(els);
  });

  // ─── Element helpers ─────────────────────────────────────────────────────

  function plumEls(refEl) {
    const c = refEl.closest('.new-pincode-outer.cart-drawer-only');
    if (!c) return null;
    const input = c.querySelector('.pincode_input');
    const tick  = c.querySelector('.verified');
    const btn   = c.querySelector('.delivery-check');
    const msg   = c.querySelector('.expected');
    return input && tick && btn && msg ? { input, tick, btn, msg } : null;
  }

  function plumReset({ input, tick, btn, msg }, focusInput) {
    input.classList.remove('is-verified', 'is-error');
    input.removeAttribute('readonly');
    tick.style.display  = 'none';
    btn.style.display   = 'inline-flex';
    btn.textContent     = 'Check';
    btn.classList.remove('is-change');
    msg.style.display   = 'none';
    msg.innerHTML       = '';
    msg.classList.remove('error');
    if (focusInput) { input.focus(); input.select(); }
  }

  function plumValidate({ input, tick, btn, msg }) {
    const pin   = input.value.trim();
    const valid = /^[1-9][0-9]{5}$/.test(pin);

    input.classList.toggle('is-verified', valid);
    input.classList.toggle('is-error', !valid);
    tick.style.display = valid ? 'block' : 'none';

    if (!valid) {
      btn.textContent = 'Check';
      btn.classList.remove('is-change');
      msg.style.display = pin.length === 6 ? 'block' : 'none';
      msg.innerHTML     = pin.length === 6 ? 'Enter a valid pincode.' : '';
      msg.classList.toggle('error', pin.length === 6);
      return;
    }

    localStorage.removeItem(CACHE_KEY);
    plumFetch(pin, { input, tick, btn, msg });
  }

  // ─── API fetch ───────────────────────────────────────────────────────────

  function plumFetch(pin, { input, tick, btn, msg }) {
    if (input.dataset.fetching === pin) return;
    input.dataset.fetching = pin;

    msg.style.display = 'block';
    msg.classList.remove('error');
    msg.innerHTML = 'Checking delivery date...';

    fetch('https://neworder.plumgoodness.com/api/get-estimate-date', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ drop_pincode: pin })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        const saved = { pin, date: res.expects_min_date };
        localStorage.setItem(CACHE_KEY, JSON.stringify(saved));
        plumApplyResult({ input, tick, btn, msg }, saved);
        delete input.dataset.fetching;
      })
      .catch(function () {
        msg.innerHTML = 'Unable to fetch delivery date.';
        msg.classList.add('error');
        msg.style.display = 'block';
        input.removeAttribute('readonly');
        tick.style.display = 'none';
        btn.textContent    = 'Check';
        btn.classList.remove('is-change');
        delete input.dataset.fetching;
      });
  }

  // ─── Apply cached / fresh result ─────────────────────────────────────────

  function plumApplyResult({ input, tick, btn, msg }, saved) {
    if (!saved || !saved.pin || !saved.date) return;

    const date = new Date(saved.date);
    date.setDate(date.getDate() + 1);

    input.value = saved.pin;
    input.setAttribute('readonly', true);
    input.classList.add('is-verified');
    input.classList.remove('is-error');
    tick.style.display  = 'block';
    btn.style.display   = 'inline-flex';
    btn.textContent     = 'Change';
    btn.classList.add('is-change');
    msg.innerHTML       = 'Delivers by <strong>' + plumFormatDate(date) + '</strong>';
    msg.classList.remove('error');
    msg.style.display   = 'block';
  }

  function plumFormatDate(date) {
    const d   = date.getDate();
    const sfx = [11, 12, 13].includes(d)
      ? 'th'
      : (['st', 'nd', 'rd'][(d % 10) - 1] || 'th');
    return date.toLocaleString('en-IN', { weekday: 'short' }) +
      ', ' + d + sfx + ' ' +
      date.toLocaleString('en-IN', { month: 'short' });
  }

  // ─── Init ────────────────────────────────────────────────────────────────

  function plumInit() {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) return;

    const container = document.querySelector(SELECTOR);
    if (!container) return;

    const els = plumEls(container.querySelector('.pincode_input') || container);
    if (!els) return;

    if (!els.input.value.trim()) {
      plumApplyResult(els, JSON.parse(cached));
    }
  }

  const plumObserver = new MutationObserver(function () { plumInit(); });

  document.addEventListener('DOMContentLoaded', function () {
    plumInit();
    const cartDrawer =
      document.querySelector('.cart-drawer') ||
      document.querySelector('#cart-drawer') ||
      document.body;
    plumObserver.observe(cartDrawer, { childList: true, subtree: true });
  });

  window.plumCartDrawerPincodeInit = plumInit;
  document.documentElement.addEventListener('cart:refresh', plumInit);
})();
